import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { openWhatsApp } from '../../utils/whatsApp';

const TEMPLATE_KEYS = ['blank', 'promo', 'renewal', 'welcome'];

export default function MemberWhatsAppComposer({ memberName, memberPhone }) {
  const { t } = useTranslation();
  const [templateKey, setTemplateKey] = useState('blank');
  const [message, setMessage] = useState('');

  const templateMessage = useMemo(() => {
    if (templateKey === 'blank') return '';
    const name = memberName || t('gym.membersManager.communications.whatsapp.defaultMemberName');
    return t(`gym.membersManager.communications.whatsapp.templates.${templateKey}`, { name });
  }, [templateKey, memberName, t]);

  const effectiveMessage = templateKey === 'blank' ? message : templateMessage;

  const handleTemplateChange = (key) => {
    setTemplateKey(key);
    if (key !== 'blank') {
      setMessage('');
    }
  };

  const handleOpen = () => {
    if (!memberPhone || !String(memberPhone).trim()) {
      toast.error(t('gym.resetPasswordModal.noPhoneHint'));
      return;
    }
    if (!openWhatsApp(memberPhone, effectiveMessage)) {
      toast.error(t('gym.resetPasswordModal.noPhoneHint'));
    }
  };

  const hasPhone = !!(memberPhone && String(memberPhone).trim());

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-gray-900/80">
      <div className="flex items-start gap-2">
        <MessageCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {t('gym.membersManager.communications.whatsapp.title')}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-white/55">
            {t('gym.membersManager.communications.whatsapp.subtitle')}
          </p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <label
            htmlFor="wa-template"
            className="mb-1 block text-xs font-medium text-gray-600 dark:text-white/70"
          >
            {t('gym.membersManager.communications.whatsapp.templateLabel')}
          </label>
          <select
            id="wa-template"
            value={templateKey}
            onChange={(e) => handleTemplateChange(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-white/20 dark:bg-gray-800 dark:text-white"
          >
            {TEMPLATE_KEYS.map((key) => (
              <option key={key} value={key}>
                {t(`gym.membersManager.communications.whatsapp.templateOptions.${key}`)}
              </option>
            ))}
          </select>
        </div>

        {templateKey === 'blank' ? (
          <div>
            <label
              htmlFor="wa-message"
              className="mb-1 block text-xs font-medium text-gray-600 dark:text-white/70"
            >
              {t('gym.membersManager.communications.whatsapp.messageLabel')}
            </label>
            <textarea
              id="wa-message"
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('gym.membersManager.communications.whatsapp.messagePlaceholder')}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-white/20 dark:bg-gray-800 dark:text-white dark:placeholder:text-white/40"
            />
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-white/10 dark:bg-gray-800/60 dark:text-white/85">
            <p className="whitespace-pre-wrap">{templateMessage}</p>
          </div>
        )}

        <p className="text-xs text-amber-700 dark:text-amber-200/90">
          {t('gym.membersManager.communications.whatsapp.gdprHint')}
        </p>

        <button
          type="button"
          onClick={handleOpen}
          disabled={!hasPhone}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <MessageCircle className="h-4 w-4" aria-hidden />
          {t('gym.resetPasswordModal.sendWhatsApp')}
        </button>

        {!hasPhone && (
          <p className="text-xs text-amber-700 dark:text-amber-200">
            {t('gym.resetPasswordModal.noPhoneHint')}
          </p>
        )}
      </div>
    </div>
  );
}
